import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageTransition Component - Wrapper for page transition animations
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.animation - Animation type: 'fade', 'slide-up', 'slide-down', 'scale'
 * @param {number} props.duration - Animation duration in ms
 */
const PageTransition = ({ 
  children, 
  className = '',
  animation = 'fade',
  duration = 300,
}) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [displayChildren, setDisplayChildren] = useState(children);
  const isFirstRender = useRef(true);

  // Animation classes mapping
  const animationClasses = {
    fade: {
      enter: 'opacity-0',
      enterActive: 'opacity-100 transition-opacity',
      exit: 'opacity-0 transition-opacity',
    },
    'slide-up': {
      enter: 'opacity-0 translate-y-4',
      enterActive: 'opacity-100 translate-y-0 transition-all',
      exit: 'opacity-0 -translate-y-4 transition-all',
    },
    'slide-down': {
      enter: 'opacity-0 -translate-y-4',
      enterActive: 'opacity-100 translate-y-0 transition-all',
      exit: 'opacity-0 translate-y-4 transition-all',
    },
    'scale': {
      enter: 'opacity-0 scale-95',
      enterActive: 'opacity-100 scale-100 transition-all',
      exit: 'opacity-0 scale-95 transition-all',
    },
  };

  useEffect(() => {
    // Skip animation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDisplayChildren(children);
      return;
    }

    // Start exit animation
    setIsVisible(false);

    // Wait for exit animation to complete
    const exitTimer = setTimeout(() => {
      setDisplayChildren(children);
      setIsVisible(true);
    }, duration);

    return () => clearTimeout(exitTimer);
  }, [location.pathname, duration]);

  const currentAnimation = animationClasses[animation] || animationClasses.fade;
  const content = isVisible ? children : displayChildren;

  return (
    <div
      className={`
        ${className}
        ${isVisible ? currentAnimation.enterActive : currentAnimation.exit}
      `}
      style={{ 
        transitionDuration: `${duration}ms`,
      }}
    >
      {content}
    </div>
  );
};

export { PageTransition };

/**
 * PageContainer Component - Standard page wrapper with consistent padding and max-width
 */
export const PageContainer = ({ 
  children,
  className = '',
  maxWidth = '7xl',
  animate = true,
  animation = 'fade',
}) => {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full',
    none: '',
  };

  const content = (
    <div className={`
      ${maxWidthClasses[maxWidth] || maxWidthClasses['7xl']}
      mx-auto
      ${className}
    `}>
      {children}
    </div>
  );

  if (animate) {
    return (
      <PageTransition animation={animation}>
        {content}
      </PageTransition>
    );
  }

  return content;
};

/**
 * PageHeader Component - Standard page header with title, subtitle, and actions
 */
export const PageHeader = ({
  title,
  subtitle,
  actions,
  backButton,
  className = '',
}) => {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start space-x-4">
          {backButton && (
            <button
              className="mt-1 p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
              onClick={backButton.onClick}
              aria-label={backButton.label || 'Go back'}
            >
              {backButton.icon || (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              )}
            </button>
          )}
          <div>
            <h1 className="text-2xl font-display font-bold text-gray-900">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center space-x-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * PageSection Component - Section wrapper with optional title
 */
export const PageSection = ({
  title,
  description,
  children,
  className = '',
  headerClassName = '',
}) => {
  return (
    <section className={`mb-8 ${className}`}>
      {(title || description) && (
        <div className={`mb-4 ${headerClassName}`}>
          {title && (
            <h2 className="text-lg font-semibold text-gray-900">
              {title}
            </h2>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500">
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </section>
  );
};

/**
 * PageContent Component - Main content area wrapper
 */
export const PageContent = ({ 
  children, 
  className = '' 
}) => {
  return (
    <main className={`
      p-4 lg:p-6 xl:p-8
      min-h-[calc(100vh-4rem)]
      ${className}
    `}>
      {children}
    </main>
  );
};

export default PageTransition;
